// src/lib/products/catalog.ts

import { siteConfig } from "~/lib/config/site"
import { DEFAULT_DIGITAL_OFFER_COUNTRIES } from "~/lib/ads-framework/seo"
import {
  MC_RETURN_POLICY_LABEL_DEFAULT,
  MC_RETURN_POLICY_LABEL_SKILLS,
} from "~/lib/legal/policy-urls"
// Type-only: keeps this module free of `checkout.ts`'s server-only `env`
// runtime import so it stays safe to pull into server components AND into
// the Jest (babel) test environment, which can't load the ESM env shim.
import { staticProductImagePath } from "~/lib/products/images"
import type { ProductSlug } from "~/server/stripe/checkout"

/**
 * Canonical commerce catalog for the storefront SKUs we sell.
 *
 * This is the single source of truth for the facts a shopping channel
 * needs — title, price, brand, image, product-page URL, taxonomy — and
 * it drives the Google Merchant Center product feed served at
 * `/merchant-feed.xml` (the "sync" that lands these products on the
 * free Shopping tab without depending solely on Google crawling the
 * on-page Product structured data).
 *
 * `price` is re-exported as a named constant per SKU and consumed by
 * the product pages' JSON-LD so the figure lives in exactly one place:
 * a drift between the feed price and the on-page Offer price is a real
 * Merchant Center "mismatched price" disapproval, not a cosmetic bug.
 *
 * The type imports (`ProductSlug`) are type-only, so this module pulls
 * in NO server-only Stripe runtime code — it stays safe to import from
 * server components (the product pages) without dragging the Stripe SDK
 * into their module graph. Availability is resolved by the feed route,
 * which is already a server-only surface.
 */

export interface CatalogProduct {
  /** Stable SKU slug — matches the Stripe `lookup_key` and checkout slug. */
  id: ProductSlug
  /** Product title as it should appear in the Shopping listing. */
  title: string
  /** Plain-text description (no HTML, ≤ 5000 chars per Google's spec). */
  description: string
  /** Site-relative product page path (the listing's landing page). */
  path: string
  /**
   * LIVE static square webp under `/images/storefront/<sku>/…` (your 1×1
   * assets). Social `og:image` is served by each page's `/opengraph-image`
   * route (static 2400×1260 webp primary, Satori PNG fallback) — see
   * `STOREFRONT_IMAGE_WIRING` in `~/lib/products/images`.
   */
  imagePath: string
  /** Per-unit price in major units, e.g. "199.00". */
  price: string
  /** ISO 4217 currency code. */
  priceCurrency: "USD"
  /** Brand name — recommended whenever no GTIN/MPN exists (software). */
  brand: string
  /** Google condition value. */
  condition: "new"
  /**
   * A VALID node from Google's product taxonomy. "Software > Computer
   * Software" (id 313) is a stable, real node both SKUs sit under — the
   * finer-grained classification goes in `productType` (free-form), not
   * here, because an invalid `google_product_category` triggers a feed
   * warning while `product_type` is never validated.
   */
  googleProductCategory: string
  /** Free-form merchant taxonomy (our own breadcrumb, not validated). */
  productType: string
  /**
   * Merchant Center `return_policy_label` value. Maps this SKU to the
   * default or exception return policy configured in Merchant Center.
   * @see docs/storefront/merchant-center-policies-runbook.md
   */
  returnPolicyLabel: typeof MC_RETURN_POLICY_LABEL_DEFAULT | typeof MC_RETURN_POLICY_LABEL_SKILLS
}

/** Brand emitted on every SKU — single home so it can't drift per item. */
export const PRODUCT_BRAND = "Adsights"

/** Valid Google taxonomy node shared by both software SKUs. */
const GOOGLE_CATEGORY_SOFTWARE = "Software > Computer Software"

/**
 * Per-SKU list prices. Imported by the product pages' JSON-LD so the
 * feed and the on-page Offer never advertise different numbers.
 */
export const ADS_FRAMEWORK_PRICE = "199.00"
export const ADS_SKILLS_PRICE = "99.00"
export const EMAIL_FRAMEWORK_PRICE = "149.00"
export const EMAIL_SKILLS_PRICE = "79.00"

/**
 * Display label for a catalog major-unit price string: `"199.00"` → `"$199"`
 * (non-integer prices keep their cents). Marketing surfaces (homepage
 * callout, /pricing framework block) COMPOSE their visible prices through
 * this helper from the per-SKU constants above instead of hand-typing
 * dollar figures, so a catalog price change propagates everywhere and the
 * feed / JSON-LD / on-page copy can never drift apart.
 *
 * Invalid input THROWS (rather than rendering a garbage label): every call
 * site is a server component rendered at build time, so a malformed
 * constant fails the build loudly instead of shipping "$NaN" to buyers
 * (`r3566333792`).
 */
export function formatCatalogPrice(price: string): string {
  if (!/^\d+(\.\d+)?$/.test(price)) {
    throw new Error(
      `formatCatalogPrice: expected a major-unit decimal string (e.g. "199.00"), got ${JSON.stringify(price)}`,
    )
  }
  const value = Number.parseFloat(price)
  return `$${Number.isInteger(value) ? value : price}`
}

export const PRODUCT_CATALOG = [
  {
    id: "ads-framework",
    title: "Adsights Ads Framework",
    description:
      "Agentic Remotion ads framework with 8 ad concepts × 12 platform formats and 11 Claude Code skills for the agentic loop from brief to rendered MP4. One-time purchase, lifetime engine updates.",
    path: siteConfig.baseLinks.adsFramework,
    imagePath: staticProductImagePath("ads-framework"),
    price: ADS_FRAMEWORK_PRICE,
    priceCurrency: "USD",
    brand: PRODUCT_BRAND,
    condition: "new",
    googleProductCategory: GOOGLE_CATEGORY_SOFTWARE,
    productType: "Software > Productivity > Multimedia Authoring",
    returnPolicyLabel: MC_RETURN_POLICY_LABEL_DEFAULT,
  },
  {
    id: "ads-skills-only",
    title: "Adsights Ads Skills for Claude",
    description:
      "Eight agentic ad-creative skills for Claude — storyboard, copy, voiceover, review, asset management. No code, no install: a drop-in .claude/ folder you invoke as slash commands.",
    path: siteConfig.baseLinks.adsSkills,
    imagePath: staticProductImagePath("ads-skills-only"),
    price: ADS_SKILLS_PRICE,
    priceCurrency: "USD",
    brand: PRODUCT_BRAND,
    condition: "new",
    googleProductCategory: GOOGLE_CATEGORY_SOFTWARE,
    productType: "Software > Productivity > Developer Tools",
    returnPolicyLabel: MC_RETURN_POLICY_LABEL_SKILLS,
  },
  {
    id: "email-framework",
    title: "Adsights Email Framework",
    description:
      "Agentic React Email framework — 6 lifecycle templates, brand-token-aware primitives, and Claude Code skills that take you from brief to inbox-safe HTML + plaintext, rendered locally. One-time purchase, lifetime updates.",
    path: siteConfig.baseLinks.emailFramework,
    imagePath: staticProductImagePath("email-framework"),
    price: EMAIL_FRAMEWORK_PRICE,
    priceCurrency: "USD",
    brand: PRODUCT_BRAND,
    condition: "new",
    googleProductCategory: GOOGLE_CATEGORY_SOFTWARE,
    productType: "Software > Productivity > Email",
    returnPolicyLabel: MC_RETURN_POLICY_LABEL_DEFAULT,
  },
  {
    id: "email-skills-only",
    title: "Adsights Email Skills for Claude",
    description:
      "Agentic email-creative skills for Claude — plan, copy, scaffold, and adversarially review marketing emails as drop-in Claude Code slash commands. Zip-only, no engine. One-time purchase.",
    path: siteConfig.baseLinks.emailSkills,
    imagePath: staticProductImagePath("email-skills-only"),
    price: EMAIL_SKILLS_PRICE,
    priceCurrency: "USD",
    brand: PRODUCT_BRAND,
    condition: "new",
    googleProductCategory: GOOGLE_CATEGORY_SOFTWARE,
    productType: "Software > Productivity > Developer Tools",
    returnPolicyLabel: MC_RETURN_POLICY_LABEL_SKILLS,
  },
] as const satisfies readonly CatalogProduct[]

/**
 * Compile-time exhaustiveness guard: every sellable SKU in `PRODUCT_SLUGS`
 * MUST have a catalog entry above, so a future SKU addition (like #186's
 * email products) can't silently ship without a Shopping-tab listing.
 * Add a slug to `PRODUCT_SLUGS` without a catalog row and this becomes a
 * type error pointing at the missing id.
 */
type _MissingFromCatalog = Exclude<ProductSlug, (typeof PRODUCT_CATALOG)[number]["id"]>
const _everySkuIsListed: _MissingFromCatalog extends never ? true : _MissingFromCatalog = true
void _everySkuIsListed

/**
 * Countries the feed advertises free (digital) shipping to. Mirrors the
 * structured data's `shippingDetails` coverage
 * (`DEFAULT_DIGITAL_OFFER_COUNTRIES`) so the two surfaces describe the
 * same sales geography to Google.
 */
export const FEED_SHIPPING_COUNTRIES = DEFAULT_DIGITAL_OFFER_COUNTRIES

/** Resolve a site-relative path to an absolute URL on the canonical origin. */
export function absoluteUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`
  return `${siteConfig.url}${p}`
}

/** Absolute product-page URL for a catalog entry. */
export function productLink(product: CatalogProduct): string {
  return absoluteUrl(product.path)
}

/** Absolute image URL for a catalog entry. */
export function productImageLink(product: CatalogProduct): string {
  return absoluteUrl(product.imagePath)
}

/**
 * Look up a catalog entry by SKU slug.
 *
 * @param {ProductSlug} id - Stable SKU slug matching checkout and the feed.
 * @returns {CatalogProduct} The canonical listing facts for that SKU.
 * @throws {Error} When no catalog row exists for the slug.
 */
export function getCatalogProduct(id: ProductSlug): CatalogProduct {
  const product = PRODUCT_CATALOG.find((entry) => entry.id === id)
  if (!product) {
    throw new Error(`No catalog entry for SKU "${id}"`)
  }
  return product
}
